import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as FactoryIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FullScreenLayout from './layouts/FullScreenLayout';
import { productionPlantsApi } from '../services/productionPlantsApi';

// Styled components for modern black and white design
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: 
    status === 'active' ? '#000000' :
    status === 'inactive' ? '#757575' :
    status === 'maintenance' ? '#ff9800' :
    '#f44336',
  color: '#ffffff',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #e0e0e0',
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#333333',
  },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(1, 0),
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: '8px',
}));

const ProductionPlants = () => {
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [plantToDelete, setPlantToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    status: 'active'
  });

  const statusOptions = [
    { value: 'active', label: 'Active', icon: <CheckCircleIcon /> },
    { value: 'inactive', label: 'Inactive', icon: <CancelIcon /> },
    { value: 'maintenance', label: 'Maintenance', icon: <BuildIcon /> },
    { value: 'closed', label: 'Closed', icon: <CloseIcon /> }
  ];

  useEffect(() => {
    fetchPlants();
  }, []);

  const fetchPlants = async () => {
    try {
      setLoading(true);
      const response = await productionPlantsApi.getAllPlants();
      const plantsData = response.data || [];
      setPlants(plantsData);
    } catch (error) {
      console.error('Error fetching plants:', error);
      showSnackbar('Failed to fetch production plants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (plant = null) => {
    if (plant) {
      setEditingPlant(plant);
      setFormData({
        name: plant.name || '',
        description: plant.description || '',
        location: plant.location || '',
        status: plant.status || 'active'
      });
    } else {
      setEditingPlant(null);
      setFormData({
        name: '',
        description: '',
        location: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlant(null);
    setFormData({
      name: '',
      description: '',
      location: '',
      status: 'active'
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        showSnackbar('Plant name is required', 'error');
        return;
      }

      if (editingPlant) {
        await productionPlantsApi.updatePlant(editingPlant.plant_id, formData);
        showSnackbar('Production plant updated successfully');
      } else {
        await productionPlantsApi.createPlant(formData);
        showSnackbar('Production plant created successfully');
      }

      handleCloseDialog();
      fetchPlants();
    } catch (error) {
      console.error('Error saving plant:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to save production plant',
        'error'
      );
    }
  };

  const handleDeleteClick = (plant) => {
    setPlantToDelete(plant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productionPlantsApi.deletePlant(plantToDelete.plant_id);
      showSnackbar('Production plant deleted successfully');
      setDeleteDialogOpen(false);
      setPlantToDelete(null);
      fetchPlants();
    } catch (error) {
      console.error('Error deleting plant:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to delete production plant',
        'error'
      );
    }
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.icon : <CheckCircleIcon />;
  };

  const renderPlantCard = (plant) => (
    <Grid item xs={12} sm={6} lg={4} key={plant.plant_id}>
      <StyledCard onClick={() => navigate(`/production-plants/${plant.plant_id}`)}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FactoryIcon sx={{ color: '#000000', fontSize: '1.5rem' }} />
              <Typography variant="h6" fontWeight={600} color="#000000">
                {plant.name}
              </Typography>
            </Box>
            <StyledChip
              label={plant.status}
              status={plant.status}
              icon={getStatusIcon(plant.status)}
              size="small"
            />
          </Box>

          {plant.description && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {plant.description}
            </Typography>
          )}

          {plant.location && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationIcon sx={{ color: '#757575', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                {plant.location}
              </Typography>
            </Box>
          )}

          {/* Sections Summary */}
          {plant.sections && plant.sections.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BusinessIcon sx={{ color: '#000000', fontSize: '1rem' }} />
                <Typography variant="body2" fontWeight={600}>
                  {plant.sections.length} Section{plant.sections.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {plant.sections
                  .sort((a, b) => a.section_order - b.section_order)
                  .map(section => section.name)
                  .join(' → ')}
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Created: {new Date(plant.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Tooltip title="Edit Plant">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(plant);
              }}
              sx={{ color: '#000000' }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Plant">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(plant);
              }}
              sx={{ color: '#f44336' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </StyledCard>
    </Grid>
  );

  const header = (
    <HeaderContainer>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#000000" gutterBottom>
          Production Plants
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your manufacturing facilities and production sections
        </Typography>
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{
          backgroundColor: '#000000',
          color: '#ffffff',
          fontWeight: 600,
          px: 3,
          py: 1.5,
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: '#333333',
          },
        }}
      >
        Add Plant
      </Button>
    </HeaderContainer>
  );

  const mainContent = (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#000000' }} />
        </Box>
      ) : plants.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          textAlign="center"
        >
          <FactoryIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Production Plants Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Get started by creating your first production plant
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333' },
            }}
          >
            Add Your First Plant
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {plants.map(renderPlantCard)}
        </Grid>
      )}
    </Box>
  );

  return (
    <>
      <FullScreenLayout
        header={header}
        mainPanel={mainContent}
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingPlant ? 'Edit Production Plant' : 'Add New Production Plant'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plant Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={3}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    '&.Mui-focused': {
                      color: '#000000',
                    },
                  }}
                >
                  Status
                </InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#000000',
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: '#757575' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            {editingPlant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Delete Production Plant
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{plantToDelete?.name}"? This action cannot be undone.
          </Typography>
          {plantToDelete?.sections?.length > 0 && (
            <Typography color="warning.main" sx={{ mt: 2 }}>
              This plant has {plantToDelete.sections.length} section(s) that will also be deleted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: '#757575' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductionPlants; 